import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Paper,
  Typography,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Avatar,
  Skeleton,
  Alert,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  ButtonBase,
  Fade,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Label,
  ResponsiveContainer,
} from "recharts";
import Cookies from "js-cookie";
import useDataUsr from "../../hooks/useDataUsr";
import DateRangeFilter, { mesActual } from "./DateRangeFilter";
import AccesosDirectos from "./AccesosDirectos";

const admin_service = require("../../helpers/admin_service");

// Roles que ven el dashboard de reporteria. El resto entra al portal y ve la
// grilla de accesos directos de su menu.
// Perfiles comerciales: ven el mismo tablero pero limitado a los inscritos
// que tienen asignados, no al total del portal.
const ROLES_ASESOR = ["COMERCIAL", "REDES SOCIALES"];

const ROLES_REPORTERIA = [
  "ADMINISTRADOR",
  "CONTROL-ACADEMICO",
  "DIRECCION-COMERCIAL",
  "SUBGERENCIA",
  "CONTABILIDAD",
];

const COLORS = ["#4da3d8", "#d9a441", "#4cc38a", "#8b7ff0", "#e5646e"];

const fmtQ = (n) =>
  "Q " + Number(n || 0).toLocaleString("es-GT", { maximumFractionDigits: 0 });

// Las fichas de inscripción se cobran en quetzales o en dólares y los montos
// de una moneda no se suman con los de la otra: el backend devuelve cada
// monto por separado (`monto` en Q, `monto_usd` en US$).
const fmtUSD = (n) =>
  "US$ " +
  Number(n || 0).toLocaleString("es-GT", { maximumFractionDigits: 0 });

const fmtMoneda = (n, moneda) => (moneda === "USD" ? fmtUSD(n) : fmtQ(n));

// Campo del endpoint según la opción del selector de moneda: quetzales,
// dólares o consolidado en quetzales (dólares convertidos con el tipo de cambio
// del consolidado, ver TIPO_CAMBIO_CONSOLIDADO_FIJO).
const campoMoneda = (moneda, gtq, usd, consolidado) =>
  moneda === "USD" ? usd : moneda === "CONSOLIDADO" ? consolidado : gtq;

// Tipo de cambio con el que el backend consolida los dólares en quetzales.
// Debe coincidir con TIPO_CAMBIO_CONSOLIDADO_FIJO de
// services/admin/helpers/utils/moneda.js, que es donde se hace el cálculo.
// Mientras tenga valor, los avisos dicen "1 US$ = Q 8". Para volver al tipo de
// cambio de referencia del Banguat guardado en cada ficha, dejarlo en null
// aquí, en el backend y en
// front/ebg-administrativo/src/utils/moneda/consolidado.js: los avisos
// vuelven solos al texto del Banguat.
const TIPO_CAMBIO_CONSOLIDADO_FIJO = 8;

const CONVERSION_CONSOLIDADO =
  TIPO_CAMBIO_CONSOLIDADO_FIJO !== null
    ? `con un tipo de cambio fijo de 1 US$ = Q ${TIPO_CAMBIO_CONSOLIDADO_FIJO}`
    : "con el tipo de cambio de referencia del Banguat del día de inscripción de cada ficha";

const TOOLTIP_STYLE = {
  background: "#0e1a26",
  border: "1px solid #23394d",
  borderRadius: 8,
  fontSize: 12,
};
// Recharts pinta cada fila del tooltip con el color de su serie y, cuando la
// serie no trae color (las gráficas de pie y dona), con negro (#000), que no
// se lee sobre el fondo oscuro del tooltip. Se fija blanco en todas.
const TOOLTIP_TEXTO = { color: "#fff" };

const Dashboard = () => {
  const { dataUsr } = useDataUsr();
  const [modo, setModo] = useState("mes");
  const [rango, setRango] = useState(mesActual());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Pestaña activa de la tabla "Últimas inscripciones": 0 = pagados, 1 = cortesías.
  const [tabUltimas, setTabUltimas] = useState(0);
  // Moneda que muestra la gráfica "Ingresos por día": "GTQ", "USD" o
  // "CONSOLIDADO".
  const [monedaIngresos, setMonedaIngresos] = useState("CONSOLIDADO");
  const campoIngresos = campoMoneda(
    monedaIngresos,
    "monto",
    "monto_usd",
    "monto_consolidado"
  );

  const esAsesor = dataUsr && ROLES_ASESOR.includes(dataUsr.permisos);
  // El asesor entra al tablero solo cuando ya se resolvió su asesor_id
  // (useDataUsr lo carga aparte); sin él las consultas traerían las cifras de
  // todo el portal, que es justo lo que no debe ver.
  const asesorId = esAsesor ? dataUsr.asesor_id : null;
  const puedeVerReporteria =
    dataUsr &&
    (ROLES_REPORTERIA.includes(dataUsr.permisos) || (esAsesor && !!asesorId));

  const cargar = useCallback(() => {
    const { fecha_inicio, fecha_fin } = rango;
    if (!fecha_inicio || !fecha_fin) return;

    let jwt;
    try {
      const raw = Cookies.get(process.env.REACT_APP_COOKIE_KEY);
      jwt = raw ? JSON.parse(raw) : null;
    } catch (e) {
      jwt = null;
    }
    if (!jwt) return;

    setLoading(true);
    setError(null);

    // El :asesor final es opcional en el backend: si va, todas las cifras se
    // limitan a las fichas de ese asesor.
    const sufijoAsesor = asesorId ? `/${asesorId}` : "";
    const get = (recurso) =>
      admin_service.getDataAuth(
        `/dashboard/${recurso}/${fecha_inicio}/${fecha_fin}${sufijoAsesor}`,
        jwt.TokenType,
        jwt.accessToken
      );

    Promise.all([
      get("resumen"),
      get("ingresos-dia"),
      get("top-eventos"),
      get("distribucion"),
      get("ultimas-inscripciones"),
      get("acumulado-categoria"),
      get("inscritos-categoria"),
      // Si este recurso falla (por ejemplo, mientras el backend aún no tiene
      // el endpoint) solo se queda vacío su panel, no todo el dashboard.
      get("clasificacion").catch((err) => {
        console.log(err);
        return { data: { response: { result: [] } } };
      }),
    ])
      .then(([resumen, ingresos, top, dist, ultimas, acumulado, inscritosCat, clasif]) => {
        setData({
          resumen: resumen.data.response.result[0] || {},
          ingresosDia: ingresos.data.response.result || [],
          topEventos: top.data.response.result || [],
          distribucion: dist.data.response.result || [],
          ultimasInscripciones: ultimas.data.response.result || [],
          acumuladoCategoria: acumulado.data.response.result || [],
          inscritosCategoria: inscritosCat.data.response.result || [],
          clasificacion: clasif.data.response.result || [],
        });
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setError(
          "No se pudieron cargar los datos del dashboard. Intente de nuevo."
        );
        setLoading(false);
      });
  }, [rango, asesorId]);

  useEffect(() => {
    if (puedeVerReporteria) cargar();
    else setLoading(false);
  }, [puedeVerReporteria, cargar]);

  if (!dataUsr) return <DashboardSkeleton />;

  if (!puedeVerReporteria) return <AccesosDirectos dataUsr={dataUsr} />;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
            {esAsesor ? "Mi resumen comercial" : "Resumen de operación"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            {esAsesor
              ? "Inscripciones, ingresos y eventos de tu cartera en el periodo seleccionado"
              : "Inscripciones, ingresos y eventos del periodo seleccionado"}
          </Typography>
        </Box>
        <DateRangeFilter
          modo={modo}
          rango={rango}
          onChange={(nuevoModo, nuevoRango) => {
            setModo(nuevoModo);
            setRango(nuevoRango);
          }}
        />
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      {loading || !data ? (
        <DashboardSkeleton />
      ) : (
        <Grid container spacing={2}>
          {/* Con cinco KPIs el Grid de 12 columnas no reparte parejo, así que
              esta fila usa CSS grid para que todas midan lo mismo. */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(5, 1fr)",
                },
              }}
            >
              <KpiCard
                label="Ventas del periodo"
                value={fmtQ(data.resumen.ventas_consolidado)}
                secundario={`${fmtQ(data.resumen.ventas)} + ${fmtUSD(
                  data.resumen.ventas_usd
                )}`}
                actual={data.resumen.ventas_consolidado}
                previo={data.resumen.ventas_consolidado_previo}
              />
              <KpiCard
                label="Inscripciones"
                value={Number(data.resumen.inscripciones || 0).toLocaleString(
                  "es-GT"
                )}
                actual={data.resumen.inscripciones}
                previo={data.resumen.inscripciones_previo}
              />
              <KpiCard
                label="Eventos activos"
                value={Number(data.resumen.eventos_activos || 0).toLocaleString(
                  "es-GT"
                )}
                actual={data.resumen.eventos_activos}
                previo={data.resumen.eventos_activos_previo}
              />
              <KpiCard
                label="Recaudado"
                value={fmtQ(data.resumen.recaudado_consolidado)}
                secundario={`${fmtQ(data.resumen.recaudado)} + ${fmtUSD(
                  data.resumen.recaudado_usd
                )}`}
                actual={data.resumen.recaudado_consolidado}
                previo={data.resumen.recaudado_consolidado_previo}
              />
              {/* Inscritos ya registrados para eventos que arrancan después
                  del rango seleccionado: no tiene periodo anterior contra qué
                  compararse, así que muestra en cuántos eventos se reparten. */}
              <KpiCard
                label="Próximos eventos"
                value={Number(
                  data.resumen.proximos_inscritos || 0
                ).toLocaleString("es-GT")}
                nota={`inscritos en ${Number(
                  data.resumen.proximos_eventos || 0
                ).toLocaleString("es-GT")} evento${
                  Number(data.resumen.proximos_eventos) === 1 ? "" : "s"
                } por iniciar`}
              />
            </Box>
            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1 }}>
              Ventas y recaudado consolidados en quetzales: los dólares se
              convierten {CONVERSION_CONSOLIDADO} (valor referencial). Debajo, el
              desglose por moneda.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Panel
              titulo="Ingresos por día"
              accion={
                <SelectorMoneda
                  value={monedaIngresos}
                  onChange={setMonedaIngresos}
                />
              }
            >
              {!data.ingresosDia.some((d) => Number(d[campoIngresos]) > 0) ? (
                <SinDatos />
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={data.ingresosDia}>
                    <CartesianGrid stroke="#1c2f42" strokeDasharray="3 4" />
                    <XAxis dataKey="dia" stroke="#5f7a91" fontSize={11} />
                    <YAxis
                      stroke="#5f7a91"
                      fontSize={11}
                      tickFormatter={(v) =>
                        v >= 1000 ? `${Math.round(v / 1000)}k` : v
                      }
                    />
                    <Tooltip
                      formatter={(v) => fmtMoneda(v, monedaIngresos)}
                      contentStyle={TOOLTIP_STYLE}
                      itemStyle={TOOLTIP_TEXTO}
                      labelStyle={TOOLTIP_TEXTO}
                    />
                    <Area
                      dataKey={campoIngresos}
                      name="Ingresos"
                      stroke="#4da3d8"
                      fill="#4da3d8"
                      fillOpacity={0.25}
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </Panel>
          </Grid>

          <Grid item xs={12} md={6}>
            <Panel titulo="Inscripciones por evento · Top 5">
              {data.topEventos.length === 0 ? (
                <SinDatos />
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.topEventos} layout="vertical">
                    <XAxis type="number" stroke="#5f7a91" fontSize={11} />
                    <YAxis
                      type="category"
                      dataKey="nombre"
                      width={160}
                      stroke="#8ba3b8"
                      fontSize={11}
                    />
                    <Tooltip
                      contentStyle={TOOLTIP_STYLE}
                      itemStyle={TOOLTIP_TEXTO}
                      labelStyle={TOOLTIP_TEXTO}
                    />
                    <Bar
                      dataKey="inscripciones"
                      name="Inscripciones"
                      fill="#4da3d8"
                      radius={[0, 6, 6, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Panel>
          </Grid>

          <Grid item xs={12} md={6}>
            <Panel titulo="Distribución por tipo de evento">
              {data.distribucion.length === 0 ? (
                <SinDatos />
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={data.distribucion}
                      dataKey="pct"
                      nameKey="tipo"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                    >
                      {data.distribucion.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Legend
                      verticalAlign="bottom"
                      iconType="circle"
                      wrapperStyle={{ fontSize: 12 }}
                    />
                    <Tooltip
                      formatter={(v) => `${v}%`}
                      contentStyle={TOOLTIP_STYLE}
                      itemStyle={TOOLTIP_TEXTO}
                      labelStyle={TOOLTIP_TEXTO}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Panel>
          </Grid>

          <Grid item xs={12} md={6}>
            <Panel titulo="Inscritos por categoría">
              {data.inscritosCategoria.length === 0 ? (
                <SinDatos />
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={data.inscritosCategoria}
                      dataKey="inscritos"
                      nameKey="categoria"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                    >
                      {data.inscritosCategoria.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Legend
                      verticalAlign="bottom"
                      iconType="circle"
                      wrapperStyle={{ fontSize: 12 }}
                    />
                    <Tooltip
                      formatter={(v) => `${Number(v).toLocaleString("es-GT")} inscritos`}
                      contentStyle={TOOLTIP_STYLE}
                      itemStyle={TOOLTIP_TEXTO}
                      labelStyle={TOOLTIP_TEXTO}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Panel>
          </Grid>

          <Grid item xs={12} md={6}>
            <PanelClasificacion rows={data.clasificacion} />
          </Grid>

          <Grid item xs={12}>
            <AcumuladoPorMoneda rows={data.acumuladoCategoria} />
          </Grid>

          <Grid item xs={12}>
            <Paper>
              <Typography sx={{ fontSize: 14, fontWeight: 600, p: 2.25, pb: 1 }}>
                Últimas inscripciones
              </Typography>
              {(() => {
                const pagados = data.ultimasInscripciones.filter(
                  (r) => r.estado === "Pagado"
                );
                const cortesias = data.ultimasInscripciones.filter(
                  (r) => r.estado !== "Pagado"
                );
                const activa = tabUltimas === 1 ? cortesias : pagados;
                return (
                  <>
                    <Box
                      sx={{
                        borderBottom: 1,
                        borderColor: "divider",
                        px: 2.25,
                      }}
                    >
                      <Tabs
                        value={tabUltimas}
                        onChange={(_, v) => setTabUltimas(v)}
                        sx={{ minHeight: 42 }}
                      >
                        <Tab
                          sx={{ minHeight: 42, textTransform: "none" }}
                          label={`Pagados (${pagados.length})`}
                        />
                        <Tab
                          sx={{ minHeight: 42, textTransform: "none" }}
                          label={`Cortesías (${cortesias.length})`}
                        />
                      </Tabs>
                    </Box>
                    <TablaUltimasInscripciones rows={activa} />
                  </>
                );
              })()}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

const iniciales = (nombre) =>
  (nombre || "")
    .split(" ")
    .filter(Boolean)
    .map((parte) => parte[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

// La variacion se calcula contra el periodo anterior de igual duracion, que el
// endpoint /dashboard/resumen ya devuelve junto con el valor actual.
// `nota` reemplaza la comparación cuando el KPI no tiene periodo anterior
// contra el cual medirse (por ejemplo, lo que está por venir).
// `secundario` es una línea bajo el valor principal (el desglose Q + US$ de un
// KPI consolidado); la variación se calcula sobre `actual`/`previo`.
const KpiCard = ({ label, value, secundario, actual, previo, nota }) => {
  const anterior = Number(previo || 0);
  const ahora = Number(actual || 0);
  const hayBase = anterior > 0;
  const delta = hayBase ? ((ahora - anterior) / anterior) * 100 : null;
  const positivo = delta !== null && delta >= 0;

  return (
    <Paper
      sx={{ p: 2.25, display: "flex", flexDirection: "column", gap: 1, height: "100%" }}
    >
      <Typography
        sx={{ fontSize: 12.5, color: "text.secondary", fontWeight: 500 }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 26,
          fontWeight: 700,
          fontFamily: "'IBM Plex Mono', monospace",
        }}
      >
        {value}
      </Typography>
      {secundario && (
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            mt: -0.75,
            color: "text.secondary",
            fontFamily: "'IBM Plex Mono', monospace",
          }}
        >
          {secundario}
        </Typography>
      )}
      {nota ? (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {nota}
        </Typography>
      ) : (
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color:
              delta === null ? "text.secondary" : positivo ? "#4cc38a" : "#e5646e",
          }}
        >
          {delta === null
            ? "sin base de comparación"
            : `${positivo ? "+" : ""}${delta.toFixed(1)}%`}{" "}
          <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>
            vs periodo anterior
          </Box>
        </Typography>
      )}
    </Paper>
  );
};

// `accion` va alineado a la derecha del título (p. ej. el selector de moneda).
const Panel = ({ titulo, accion, children }) => (
  <Paper sx={{ p: 2.25 }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        mb: 1,
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{titulo}</Typography>
      {accion}
    </Box>
    {children}
  </Paper>
);

const SinDatos = ({ texto = "No hay movimientos en el periodo seleccionado." }) => (
  <Typography
    sx={{ fontSize: 13, color: "text.secondary", py: 4, textAlign: "center" }}
  >
    {texto}
  </Typography>
);

// Alterna una gráfica de montos entre quetzales, dólares y el consolidado en
// quetzales.
const SelectorMoneda = ({ value, onChange }) => (
  <ToggleButtonGroup
    value={value}
    exclusive
    size="small"
    onChange={(_event, nueva) => nueva && onChange(nueva)}
  >
    <ToggleButton value="GTQ" sx={{ px: 1.5, py: 0.25, fontSize: 12 }}>
      Q
    </ToggleButton>
    <ToggleButton value="USD" sx={{ px: 1.5, py: 0.25, fontSize: 12 }}>
      US$
    </ToggleButton>
    <ToggleButton
      value="CONSOLIDADO"
      sx={{ px: 1.5, py: 0.25, fontSize: 12, textTransform: "none" }}
    >
      Consolidado
    </ToggleButton>
  </ToggleButtonGroup>
);

const MONO = "'IBM Plex Mono', monospace";

// Vistas de "Acumulado en el periodo seleccionado". Cada una lleva su color en
// todo el bloque: tarjeta, barra de composición y mapa de calor de la tabla.
const VISTAS_ACUMULADO = [
  {
    id: "CONSOLIDADO",
    campo: "monto_consolidado",
    etiqueta: "Consolidado",
    simbolo: "Q",
    acento: "#4da3d8",
    vacio: "No hay movimientos en el periodo seleccionado.",
  },
  {
    id: "GTQ",
    campo: "monto",
    etiqueta: "Quetzales",
    simbolo: "Q",
    acento: "#4cc38a",
    vacio: "No hay ingresos en quetzales en el periodo seleccionado.",
  },
  {
    id: "USD",
    campo: "monto_usd",
    etiqueta: "Dólares",
    simbolo: "US$",
    acento: "#d9a441",
    vacio: "No hay ingresos en dólares en el periodo seleccionado.",
  },
];

const fmtPct = (n) =>
  `${Number(n || 0).toLocaleString("es-GT", { maximumFractionDigits: 1 })}%`;

// Bloque "Acumulado en el periodo seleccionado": tres tarjetas que funcionan
// como pestañas (consolidado, quetzales, dólares) con su total y una barra de
// composición, y debajo la tabla dinámica de la vista elegida.
//
// Los dólares ya convertidos a quetzales salen de restar: consolidado - Q, que
// es justo lo que las fichas en dólares suman al consolidado.
const AcumuladoPorMoneda = ({ rows }) => {
  const [vista, setVista] = useState("CONSOLIDADO");

  const suma = (campo) =>
    (rows || []).reduce((s, r) => s + Number(r[campo] || 0), 0);
  const totalQ = suma("monto");
  const totalUsd = suma("monto_usd");
  const totalConsolidado = suma("monto_consolidado");
  const usdEnQ = Math.max(totalConsolidado - totalQ, 0);
  const pct = (parte) =>
    totalConsolidado > 0 ? (parte / totalConsolidado) * 100 : 0;
  const pctQ = pct(totalQ);
  const pctUsd = pct(usdEnQ);

  const [consolidado, quetzales, dolares] = VISTAS_ACUMULADO;
  const resumen = {
    CONSOLIDADO: {
      total: fmtQ(totalConsolidado),
      detalle: "Quetzales + dólares convertidos",
      barra: [
        { valor: pctQ, color: quetzales.acento },
        { valor: pctUsd, color: dolares.acento },
      ],
      leyenda: `Q ${fmtPct(pctQ)} · US$ ${fmtPct(pctUsd)}`,
    },
    GTQ: {
      total: fmtQ(totalQ),
      detalle: `${fmtPct(pctQ)} del consolidado`,
      barra: [{ valor: pctQ, color: quetzales.acento }],
    },
    USD: {
      total: fmtUSD(totalUsd),
      detalle: `≈ ${fmtQ(usdEnQ)} · ${fmtPct(pctUsd)} del consolidado`,
      barra: [{ valor: pctUsd, color: dolares.acento }],
    },
  };
  const actual = VISTAS_ACUMULADO.find((v) => v.id === vista) || consolidado;

  return (
    <Paper sx={{ overflow: "hidden" }}>
      <Box sx={{ p: 2.25, pb: 1.75 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
          Acumulado en el periodo seleccionado
        </Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
          Ingresos de las fichas por categoría de entidad y tipo de evento. El
          consolidado convierte los dólares {CONVERSION_CONSOLIDADO} (valor
          referencial).
        </Typography>
      </Box>

      <Box
        role="tablist"
        aria-label="Moneda del acumulado"
        sx={{
          display: "grid",
          gap: 1.5,
          px: 2.25,
          pb: 2.25,
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
        }}
      >
        {VISTAS_ACUMULADO.map((v) => (
          <TarjetaVista
            key={v.id}
            vista={v}
            activa={v.id === actual.id}
            onClick={() => setVista(v.id)}
            {...resumen[v.id]}
          />
        ))}
      </Box>

      <Box
        role="tabpanel"
        sx={{ borderTop: 1, borderColor: "divider", position: "relative" }}
      >
        {/* Línea de color de la vista activa sobre la tabla. */}
        <Box
          sx={{
            position: "absolute",
            top: -1,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg, ${actual.acento}, ${alpha(
              actual.acento,
              0
            )})`,
            transition: "background .3s ease",
          }}
        />
        <Fade in key={actual.id} timeout={350}>
          <Box>
            <TablaAcumuladoCategoria
              rows={rows}
              campo={actual.campo}
              acento={actual.acento}
              simbolo={actual.simbolo}
              vacio={actual.vacio}
            />
          </Box>
        </Fade>
      </Box>
    </Paper>
  );
};

// Tarjeta-pestaña de una vista del acumulado: etiqueta, total, detalle y una
// barra con lo que esa vista representa del consolidado (la del consolidado va
// partida en quetzales y dólares).
const TarjetaVista = ({ vista, activa, onClick, total, detalle, barra, leyenda }) => (
  <ButtonBase
    role="tab"
    aria-selected={activa}
    onClick={onClick}
    sx={{
      position: "relative",
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      justifyContent: "flex-start",
      textAlign: "left",
      gap: 0.75,
      p: 1.75,
      pt: 2,
      borderRadius: 2,
      border: 1,
      borderColor: activa ? vista.acento : "divider",
      bgcolor: activa ? alpha(vista.acento, 0.1) : "transparent",
      boxShadow: activa
        ? `0 10px 28px ${alpha(vista.acento, 0.14)}`
        : "none",
      overflow: "hidden",
      transition:
        "background-color .2s ease, border-color .2s ease, box-shadow .2s ease, transform .2s ease",
      "&:hover": {
        bgcolor: alpha(vista.acento, activa ? 0.12 : 0.05),
        borderColor: alpha(vista.acento, activa ? 1 : 0.55),
        transform: activa ? "none" : "translateY(-1px)",
      },
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        bgcolor: vista.acento,
        opacity: activa ? 1 : 0.25,
        transition: "opacity .2s ease",
      },
      "&.Mui-focusVisible": {
        outline: `2px solid ${vista.acento}`,
        outlineOffset: 2,
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      <Typography
        sx={{
          fontSize: 11.5,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: activa ? vista.acento : "text.secondary",
        }}
      >
        {vista.etiqueta}
      </Typography>
      <Box
        component="span"
        sx={{
          fontSize: 11,
          fontWeight: 700,
          fontFamily: MONO,
          px: 0.75,
          borderRadius: 1,
          color: vista.acento,
          bgcolor: alpha(vista.acento, 0.14),
        }}
      >
        {vista.simbolo}
      </Box>
    </Box>
    <Typography
      sx={{ fontSize: 22, fontWeight: 700, fontFamily: MONO, lineHeight: 1.2 }}
    >
      {total}
    </Typography>
    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
      {detalle}
    </Typography>
    <Box
      sx={{
        display: "flex",
        height: 6,
        mt: 0.5,
        borderRadius: 3,
        overflow: "hidden",
        bgcolor: alpha("#8ba3b8", 0.15),
      }}
    >
      {barra.map((segmento, i) => (
        <Box
          key={i}
          sx={{
            width: `${segmento.valor}%`,
            bgcolor: segmento.color,
            transition: "width .5s ease",
          }}
        />
      ))}
    </Box>
    {leyenda && (
      <Typography sx={{ fontSize: 11, color: "text.secondary", fontFamily: MONO }}>
        {leyenda}
      </Typography>
    )}
  </ButtonBase>
);

// Colores fijos por clasificación del participante; cualquier otra
// clasificación toma uno de COLORS.
const SIN_CLASIFICACION = "Sin clasificación";
const COLORES_CLASIFICACION = {
  Nacional: "#4da3d8",
  Internacional: "#2fa8a1",
  [SIN_CLASIFICACION]: "#8aa0b2",
};
const ORDEN_CLASIFICACION = ["Nacional", "Internacional"];

// Nacional, Internacional, el resto en orden alfabético y al final los que no
// tienen clasificación.
const ordenarClasificaciones = (a, b) => {
  if (a === b) return 0;
  if (a === SIN_CLASIFICACION) return 1;
  if (b === SIN_CLASIFICACION) return -1;
  const ia = ORDEN_CLASIFICACION.indexOf(a);
  const ib = ORDEN_CLASIFICACION.indexOf(b);
  if (ia !== -1 || ib !== -1)
    return (ia === -1 ? Infinity : ia) - (ib === -1 ? Infinity : ib);
  return `${a}`.localeCompare(`${b}`, "es");
};

// Panel "Inscritos e ingresos por clasificación" (Nacional / Internacional de
// inscripcion_detalle). El endpoint devuelve por clasificación los inscritos y
// los ingresos, donde cada participante aporta Inversion / participantes de su
// ficha, separados en `monto` (Q), `monto_usd` y `monto_consolidado`. Dos donas
// (inscritos e ingresos en la moneda elegida) con el mismo color por
// clasificación y, debajo, la tabla con cantidades y porcentajes.
const PanelClasificacion = ({ rows }) => {
  const [moneda, setMoneda] = useState("CONSOLIDADO");
  const campo = campoMoneda(moneda, "monto", "monto_usd", "monto_consolidado");
  const etiquetaMoneda = campoMoneda(moneda, "Q", "US$", "consolidado Q");

  const filas = [...(rows || [])]
    .sort((a, b) => ordenarClasificaciones(a.clasificacion, b.clasificacion))
    .map((r, i) => ({
      nombre: r.clasificacion,
      inscritos: Number(r.inscritos || 0),
      ingresos: Number(r[campo] || 0),
      color: COLORES_CLASIFICACION[r.clasificacion] || COLORS[i % COLORS.length],
    }));
  const totalInscritos = filas.reduce((s, f) => s + f.inscritos, 0);
  const totalIngresos = filas.reduce((s, f) => s + f.ingresos, 0);

  return (
    <Panel
      titulo="Inscritos e ingresos por clasificación"
      accion={<SelectorMoneda value={moneda} onChange={setMoneda} />}
    >
      {filas.length === 0 ? (
        <SinDatos />
      ) : (
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
          }}
        >
          <DonaClasificacion
            titulo="Inscritos"
            datos={filas.filter((f) => f.inscritos > 0)}
            dataKey="inscritos"
            total={totalInscritos}
            centro={totalInscritos.toLocaleString("es-GT")}
            subtitulo="inscritos"
            formato={(v) => `${Number(v).toLocaleString("es-GT")} inscritos`}
          />
          <DonaClasificacion
            titulo={`Ingresos · ${etiquetaMoneda}`}
            datos={filas.filter((f) => f.ingresos > 0)}
            dataKey="ingresos"
            total={totalIngresos}
            centro={fmtMoneda(totalIngresos, moneda)}
            subtitulo="ingresos"
            formato={(v) => fmtMoneda(v, moneda)}
            vacio={
              moneda === "USD"
                ? "Sin ingresos en dólares"
                : moneda === "GTQ"
                ? "Sin ingresos en quetzales"
                : "Sin ingresos"
            }
          />
          <Box sx={{ gridColumn: "1 / -1" }}>
            <TablaClasificacion
              filas={filas}
              totalInscritos={totalInscritos}
              totalIngresos={totalIngresos}
              moneda={moneda}
            />
          </Box>
        </Box>
      )}
    </Panel>
  );
};

// Dona de una métrica por clasificación con el total al centro.
const DonaClasificacion = ({
  titulo,
  datos,
  dataKey,
  total,
  centro,
  subtitulo,
  formato,
  vacio = "Sin datos",
}) => (
  <Box>
    <Typography
      sx={{
        fontSize: 11.5,
        fontWeight: 700,
        letterSpacing: 0.6,
        textTransform: "uppercase",
        color: "text.secondary",
        textAlign: "center",
      }}
    >
      {titulo}
    </Typography>
    {datos.length === 0 ? (
      <SinDatos texto={vacio} />
    ) : (
      <ResponsiveContainer width="100%" height={190}>
        <PieChart>
          <Pie
            data={datos}
            dataKey={dataKey}
            nameKey="nombre"
            innerRadius={56}
            outerRadius={80}
            paddingAngle={2}
            stroke="none"
          >
            {datos.map((d) => (
              <Cell key={d.nombre} fill={d.color} />
            ))}
            <Label
              position="center"
              content={({ viewBox }) => (
                <g>
                  <text
                    x={viewBox.cx}
                    y={viewBox.cy - 4}
                    textAnchor="middle"
                    fill="currentColor"
                    fontSize={14}
                    fontWeight={700}
                    fontFamily={MONO}
                  >
                    {centro}
                  </text>
                  <text
                    x={viewBox.cx}
                    y={viewBox.cy + 14}
                    textAnchor="middle"
                    fill="#8ba3b8"
                    fontSize={11}
                  >
                    {subtitulo}
                  </text>
                </g>
              )}
            />
          </Pie>
          <Tooltip
            formatter={(v) =>
              `${formato(v)} · ${fmtPct(total > 0 ? (v / total) * 100 : 0)}`
            }
            contentStyle={TOOLTIP_STYLE}
            itemStyle={TOOLTIP_TEXTO}
            labelStyle={TOOLTIP_TEXTO}
          />
        </PieChart>
      </ResponsiveContainer>
    )}
  </Box>
);

// Tabla resumen del panel de clasificación: hace de leyenda de las dos donas
// con las cantidades exactas y su porcentaje sobre el total.
const TablaClasificacion = ({ filas, totalInscritos, totalIngresos, moneda }) => {
  const pct = (v, t) => fmtPct(t > 0 ? (v / t) * 100 : 0);
  const borde = { borderTop: 2, borderColor: "divider" };
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table
        size="small"
        sx={{
          "& th, & td": { whiteSpace: "nowrap" },
          "& tbody td:not(:first-of-type)": { fontFamily: MONO },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Clasificación</TableCell>
            <TableCell align="right">Inscritos</TableCell>
            <TableCell align="right">%</TableCell>
            <TableCell align="right">Ingresos</TableCell>
            <TableCell align="right">%</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filas.map((f) => (
            <TableRow key={f.nombre} hover>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: f.color,
                      flexShrink: 0,
                    }}
                  />
                  {f.nombre}
                </Box>
              </TableCell>
              <TableCell align="right">
                {f.inscritos.toLocaleString("es-GT")}
              </TableCell>
              <TableCell align="right" sx={{ color: "text.secondary" }}>
                {pct(f.inscritos, totalInscritos)}
              </TableCell>
              <TableCell align="right">{fmtMoneda(f.ingresos, moneda)}</TableCell>
              <TableCell align="right" sx={{ color: "text.secondary" }}>
                {pct(f.ingresos, totalIngresos)}
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell sx={{ ...borde, fontWeight: 700 }}>Total</TableCell>
            <TableCell align="right" sx={{ ...borde, fontWeight: 700 }}>
              {totalInscritos.toLocaleString("es-GT")}
            </TableCell>
            <TableCell align="right" sx={{ ...borde }} />
            <TableCell align="right" sx={{ ...borde, fontWeight: 700 }}>
              {fmtMoneda(totalIngresos, moneda)}
            </TableCell>
            <TableCell align="right" sx={{ ...borde }} />
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

// Ingresos con dos decimales y separador de miles, sin símbolo de moneda (la
// esquina de la tabla indica la moneda de la vista). Se usa solo en la tabla de
// acumulado, que necesita el detalle al centavo.
const fmtMonto = (n) =>
  Number(n || 0).toLocaleString("es-GT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Orden fijo de las filas; las categorías que no estén en la lista (por
// ejemplo "Sin categoria") van al final en orden alfabético.
const ORDEN_CATEGORIAS = ["Bancos", "Cooperativas", "Otros"];

// Tabla dinámica "Acumulado en el periodo seleccionado": cruza la categoría de
// la ficha (filas) contra el tipo de evento (columnas) sumando ingresos. El
// endpoint devuelve la lista plana [{ categoria, tipo, monto, monto_usd,
// monto_consolidado }]; acá se arma la matriz, el total por fila, el total por
// columna y el total general. `campo` elige la vista ("monto_consolidado",
// "monto" = Q o "monto_usd" = US$); las combinaciones sin ingresos en esa
// vista no generan fila ni columna.
//
// Mapa de calor: el fondo de cada celda se intensifica con el color de la
// vista (`acento`) según su monto frente a la celda más alta; las celdas en
// cero muestran "—" para no hacer ruido.
const TablaAcumuladoCategoria = ({ rows, campo, acento, simbolo, vacio }) => {
  const conMonto = (rows || []).filter((r) => Number(r[campo] || 0) !== 0);
  if (conMonto.length === 0) return <SinDatos texto={vacio} />;

  const tipos = [];
  const categorias = [];
  const celda = {}; // `${categoria}|${tipo}` -> monto

  conMonto.forEach((r) => {
    const { categoria, tipo } = r;
    if (!tipos.includes(tipo)) tipos.push(tipo);
    if (!categorias.includes(categoria)) categorias.push(categoria);
    celda[`${categoria}|${tipo}`] = Number(r[campo] || 0);
  });

  tipos.sort((a, b) => a.localeCompare(b, "es"));
  categorias.sort((a, b) => {
    const ia = ORDEN_CATEGORIAS.indexOf(a);
    const ib = ORDEN_CATEGORIAS.indexOf(b);
    if (ia !== -1 || ib !== -1)
      return (ia === -1 ? Infinity : ia) - (ib === -1 ? Infinity : ib);
    return a.localeCompare(b, "es");
  });

  const montoCelda = (cat, tipo) => celda[`${cat}|${tipo}`] || 0;
  const totalFila = (cat) => tipos.reduce((s, t) => s + montoCelda(cat, t), 0);
  const totalColumna = (tipo) =>
    categorias.reduce((s, c) => s + montoCelda(c, tipo), 0);
  const totalGeneral = categorias.reduce((s, c) => s + totalFila(c), 0);

  const maximo = Math.max(
    0,
    ...categorias.flatMap((c) => tipos.map((t) => montoCelda(c, t)))
  );
  const fondoCelda = (monto) =>
    monto > 0 && maximo > 0
      ? alpha(acento, 0.05 + 0.33 * (monto / maximo))
      : "transparent";

  const borde = { borderTop: 2, borderColor: alpha(acento, 0.5) };

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table
        size="small"
        sx={{
          minWidth: 640,
          "& th, & td": { whiteSpace: "nowrap" },
          "& tbody td": { fontFamily: MONO },
          "& tbody td:first-of-type": {
            fontFamily: "'IBM Plex Sans', sans-serif",
            fontWeight: 700,
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                Entidad
                <Box
                  component="span"
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    fontFamily: MONO,
                    px: 0.75,
                    borderRadius: 1,
                    color: acento,
                    bgcolor: alpha(acento, 0.14),
                  }}
                >
                  {simbolo}
                </Box>
              </Box>
            </TableCell>
            {tipos.map((t) => (
              <TableCell key={t} align="right">
                {t}
              </TableCell>
            ))}
            <TableCell align="right">Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {categorias.map((cat) => (
            <TableRow key={cat} hover>
              <TableCell
                sx={{ borderLeft: 3, borderLeftColor: alpha(acento, 0.6) }}
              >
                {cat}
              </TableCell>
              {tipos.map((t) => {
                const monto = montoCelda(cat, t);
                return (
                  <TableCell
                    key={t}
                    align="right"
                    sx={{
                      bgcolor: fondoCelda(monto),
                      color: monto > 0 ? "text.primary" : "text.disabled",
                      transition: "background-color .3s ease",
                    }}
                  >
                    {monto > 0 ? fmtMonto(monto) : "—"}
                  </TableCell>
                );
              })}
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {fmtMonto(totalFila(cat))}
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell sx={{ ...borde }}>Total</TableCell>
            {tipos.map((t) => (
              <TableCell
                key={t}
                align="right"
                sx={{ ...borde, fontWeight: 700 }}
              >
                {fmtMonto(totalColumna(t))}
              </TableCell>
            ))}
            <TableCell
              align="right"
              sx={{
                ...borde,
                fontWeight: 700,
                color: acento,
                bgcolor: alpha(acento, 0.16),
              }}
            >
              {simbolo} {fmtMonto(totalGeneral)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

// Tabla de una pestaña (pagados / cortesías) de "Últimas inscripciones".
// Todas las columnas salvo "Evento" van en una sola línea; si el ancho no
// alcanza, el contenedor hace scroll horizontal.
const TablaUltimasInscripciones = ({ rows }) => (
  <Box sx={{ overflowX: "auto" }}>
    <Table
      size="small"
      sx={{ minWidth: 680, "& th, & td": { whiteSpace: "nowrap" } }}
    >
      <TableHead>
        <TableRow>
          <TableCell>Participante</TableCell>
          <TableCell>Evento</TableCell>
          <TableCell>Fecha</TableCell>
          <TableCell align="right">Monto</TableCell>
          <TableCell>Estado</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 && (
          <TableRow>
            <TableCell colSpan={5} sx={{ whiteSpace: "normal" }}>
              <SinDatos />
            </TableCell>
          </TableRow>
        )}
        {rows.map((row, i) => (
          <TableRow key={i} hover>
            <TableCell>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                }}
              >
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: 11,
                    bgcolor: "#1e3d52",
                    color: "#9ed1f2",
                  }}
                >
                  {iniciales(row.nombre)}
                </Avatar>
                {row.nombre}
              </Box>
            </TableCell>
            <TableCell sx={{ whiteSpace: "normal", minWidth: 220 }}>
              {row.evento}
            </TableCell>
            <TableCell>{row.fecha}</TableCell>
            <TableCell
              align="right"
              sx={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              {fmtMoneda(row.monto, row.moneda)}
            </TableCell>
            <TableCell>
              <Chip
                size="small"
                label={row.estado}
                color={row.estado === "Pagado" ? "success" : "info"}
                variant="outlined"
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

const DashboardSkeleton = () => (
  <Grid container spacing={2}>
    {[0, 1, 2, 3].map((i) => (
      <Grid item xs={12} sm={6} lg={3} key={i}>
        <Skeleton variant="rounded" height={112} />
      </Grid>
    ))}
    <Grid item xs={12}>
      <Skeleton variant="rounded" height={280} />
    </Grid>
    <Grid item xs={12} md={6}>
      <Skeleton variant="rounded" height={300} />
    </Grid>
    <Grid item xs={12} md={6}>
      <Skeleton variant="rounded" height={300} />
    </Grid>
  </Grid>
);

export default Dashboard;
